import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { FileText, Save, Edit, Eye, Download, Users, File, Building2, User } from 'lucide-react';
import jsPDF from 'jspdf';

const NarrativeTab = ({ caseId, caseData, user }) => {
  const [executiveSummary, setExecutiveSummary] = useState('');
  const [narrativeDetails, setNarrativeDetails] = useState('');
  const [narrativeConclusion, setNarrativeConclusion] = useState('');
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [suspects, setSuspects] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    if (caseData) {
      setExecutiveSummary(caseData.executiveSummary || '');
      setNarrativeDetails(caseData.narrativeDetails || '');
      setNarrativeConclusion(caseData.narrativeConclusion || '');
    }
  }, [caseData]);

  useEffect(() => {
    if (caseId) {
      fetchRelatedData();
    }
  }, [caseId]);

  const fetchRelatedData = async () => {
    try {
      setLoadingData(true);
      // Fetch suspects (customers and inmates)
      const suspectsResponse = await api.get(`/suspects/case/${caseId}`);
      setSuspects(suspectsResponse.data.data || []);

      // Fetch evidence/attachments
      const evidenceResponse = await api.get(`/evidence/case/${caseId}`);
      setEvidence(evidenceResponse.data.data || []);
    } catch (error) {
      console.error('Error fetching related data:', error);
      toast.error('Failed to load related data');
    } finally {
      setLoadingData(false);
    }
  };

  const canEdit = user?.role === 'Administrator' || 
                  user?.role === 'Supervisor' || 
                  (user?.role === 'Investigator' && caseData?.assignedTo?._id === user._id);

  const handleSave = async () => {
    try {
      setLoading(true);
      await api.put(`/cases/${caseId}`, {
        executiveSummary,
        narrativeDetails,
        narrativeConclusion
      });
      toast.success('Narrative report saved successfully');
      setIsEditing(false);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save narrative report');
    } finally {
      setLoading(false);
    }
  };

  const generatePDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    let yPosition = margin;

    // Helper function to add text with word wrap
    const addText = (text, fontSize, isBold = false, spacing = 6) => {
      doc.setFontSize(fontSize);
      doc.setFont('helvetica', isBold ? 'bold' : 'normal');
      
      const lines = doc.splitTextToSize(text || '', pageWidth - 2 * margin);
      lines.forEach((line) => {
        if (yPosition + spacing > pageHeight - margin - 15) {
          doc.addPage();
          yPosition = margin;
        }
        doc.text(line, margin, yPosition);
        yPosition += spacing;
      });
      yPosition += 3;
    };

    // Helper function to add section heading
    const addSectionHeading = (text) => {
      if (yPosition + 15 > pageHeight - margin - 15) {
        doc.addPage();
        yPosition = margin;
      }
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(text.toUpperCase(), margin, yPosition);
      yPosition += 8;
      // Draw line under heading
      doc.setLineWidth(0.5);
      doc.line(margin, yPosition - 2, pageWidth - margin, yPosition - 2);
      yPosition += 5;
    };

    // Title
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    const titleText = 'Narrative Investigation Report';
    doc.text(titleText, pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 8;
    
    // Date
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const dateText = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    doc.text(dateText, pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 12;

    // Draw line under header
    doc.setLineWidth(1);
    doc.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 10;

    // Case Information
    addSectionHeading('Case Information');
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    
    const caseInfo = [
      { label: 'Case Number', value: caseData?.caseNumber || 'N/A' },
      { label: 'Status', value: caseData?.status || 'N/A' },
      { label: 'Title', value: caseData?.title || 'N/A' },
      { label: 'Priority', value: caseData?.priority || 'N/A' },
      { label: 'Case Type', value: caseData?.caseType || 'N/A' },
    ];

    if (caseData?.facilityName) {
      caseInfo.push({ label: 'Facility', value: caseData.facilityName });
    }
    if (caseData?.assignedTo) {
      caseInfo.push({ label: 'Assigned To', value: `${caseData.assignedTo.firstName} ${caseData.assignedTo.lastName}` });
    }

    caseInfo.forEach((info) => {
      if (yPosition + 6 > pageHeight - margin - 15) {
        doc.addPage();
        yPosition = margin;
      }
      doc.setFont('helvetica', 'bold');
      doc.text(`${info.label}:`, margin, yPosition);
      doc.setFont('helvetica', 'normal');
      const valueLines = doc.splitTextToSize(info.value, pageWidth - margin - 60);
      doc.text(valueLines, margin + 50, yPosition);
      yPosition += valueLines.length * 6;
    });
    yPosition += 8;

    // Related Parties
    if (suspects.length > 0) {
      addSectionHeading('Related Parties');
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      
      suspects.forEach((suspect) => {
        if (yPosition + 20 > pageHeight - margin - 15) {
          doc.addPage();
          yPosition = margin;
        }
        
        doc.setFont('helvetica', 'bold');
        doc.text(suspect.suspectType + ':', margin + 5, yPosition);
        yPosition += 6;
        
        doc.setFont('helvetica', 'normal');
        if (suspect.suspectType === 'Inmate') {
          doc.text(`  Name: ${suspect.inmateName || 'N/A'}`, margin + 5, yPosition);
          yPosition += 5;
          doc.text(`  Facility: ${suspect.facilityName || 'N/A'}`, margin + 5, yPosition);
          yPosition += 5;
          doc.text(`  Inmate ID: ${suspect.inmateId || 'N/A'}`, margin + 5, yPosition);
        } else {
          doc.text(`  Name: ${suspect.customerName || 'N/A'}`, margin + 5, yPosition);
          yPosition += 5;
          if (suspect.phoneNumber) {
            doc.text(`  Phone: ${suspect.phoneNumber}`, margin + 5, yPosition);
            yPosition += 5;
          }
          if (suspect.customerId) {
            doc.text(`  Customer ID: ${suspect.customerId}`, margin + 5, yPosition);
            yPosition += 5;
          }
        }
        yPosition += 5;
      });
      yPosition += 5;
    }

    // Executive Summary
    addSectionHeading('Executive Summary');
    addText(executiveSummary || 'Not provided', 9, false, 6);

    // Details and Notes
    addSectionHeading('Details and Notes');
    addText(narrativeDetails || 'Not provided', 9, false, 6);

    // Conclusion
    addSectionHeading('Conclusion');
    addText(narrativeConclusion || 'Not provided', 9, false, 6);

    // Attachments
    if (evidence.length > 0) {
      addSectionHeading('Attachments');
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      
      evidence.forEach((item) => {
        if (yPosition + 15 > pageHeight - margin - 15) {
          doc.addPage();
          yPosition = margin;
        }
        doc.text(`• ${item.originalName}`, margin + 5, yPosition);
        yPosition += 5;
        doc.text(`  Type: ${item.fileType || 'N/A'}, Size: ${(item.fileSize / 1024).toFixed(2)} KB`, margin + 5, yPosition);
        yPosition += 5;
        if (item.description) {
          const descLines = doc.splitTextToSize(`  Description: ${item.description}`, pageWidth - margin - 10);
          descLines.forEach((line) => {
            if (yPosition + 5 > pageHeight - margin - 15) {
              doc.addPage();
              yPosition = margin;
            }
            doc.text(line, margin + 5, yPosition);
            yPosition += 5;
          });
        }
        yPosition += 3;
      });
    }

    // Footer on all pages
    const totalPages = doc.internal.pages.length - 1;
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      const footerText = `Page ${i} of ${totalPages} | Generated on ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} at ${new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
      doc.text(footerText, pageWidth / 2, pageHeight - 10, { align: 'center' });
    }

    // Save PDF
    doc.save(`Narrative_Report_${caseData?.caseNumber || caseId}_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success('PDF report downloaded successfully');
  };

  const renderPreview = () => {
    return (
      <div className="bg-white p-10 max-w-4xl mx-auto" style={{ fontFamily: 'Arial, sans-serif' }}>
        {/* Header */}
        <div className="text-center mb-10 border-b-2 border-gray-800 pb-4">
          <h1 className="text-2xl font-bold text-gray-900 mb-2 uppercase tracking-wide">Narrative Investigation Report</h1>
          <div className="text-sm text-gray-600">
            {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </div>
        </div>

        {/* Case Information */}
        <div className="mb-8">
          <h2 className="text-lg font-bold text-gray-900 mb-4 uppercase border-b border-gray-300 pb-2">Case Information</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
            <div>
              <span className="font-semibold text-gray-700">Case Number:</span>
              <span className="ml-2 text-gray-900">{caseData?.caseNumber || 'N/A'}</span>
            </div>
            <div>
              <span className="font-semibold text-gray-700">Status:</span>
              <span className="ml-2 text-gray-900">{caseData?.status || 'N/A'}</span>
            </div>
            <div className="col-span-2">
              <span className="font-semibold text-gray-700">Title:</span>
              <span className="ml-2 text-gray-900">{caseData?.title || 'N/A'}</span>
            </div>
            <div>
              <span className="font-semibold text-gray-700">Priority:</span>
              <span className="ml-2 text-gray-900">{caseData?.priority || 'N/A'}</span>
            </div>
            <div>
              <span className="font-semibold text-gray-700">Case Type:</span>
              <span className="ml-2 text-gray-900">{caseData?.caseType || 'N/A'}</span>
            </div>
            {caseData?.facilityName && (
              <div>
                <span className="font-semibold text-gray-700">Facility:</span>
                <span className="ml-2 text-gray-900">{caseData.facilityName}</span>
              </div>
            )}
            {caseData?.assignedTo && (
              <div>
                <span className="font-semibold text-gray-700">Assigned To:</span>
                <span className="ml-2 text-gray-900">{caseData.assignedTo.firstName} {caseData.assignedTo.lastName}</span>
              </div>
            )}
          </div>
        </div>

        {/* Related Parties */}
        {suspects.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-4 uppercase border-b border-gray-300 pb-2">Related Parties</h2>
            <div className="space-y-4">
              {suspects.map((suspect, index) => (
                <div key={index} className="border border-gray-300 p-4">
                  {suspect.suspectType === 'Inmate' ? (
                    <div>
                      <div className="font-semibold text-gray-900 mb-2">Inmate</div>
                      <div className="ml-4 space-y-1 text-sm">
                        <div>
                          <span className="font-medium text-gray-700">Name:</span>
                          <span className="ml-2 text-gray-900">{suspect.inmateName || 'N/A'}</span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Facility:</span>
                          <span className="ml-2 text-gray-900">{suspect.facilityName || 'N/A'}</span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Inmate ID:</span>
                          <span className="ml-2 text-gray-900 font-mono">{suspect.inmateId || 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="font-semibold text-gray-900 mb-2">Customer</div>
                      <div className="ml-4 space-y-1 text-sm">
                        <div>
                          <span className="font-medium text-gray-700">Name:</span>
                          <span className="ml-2 text-gray-900">{suspect.customerName || 'N/A'}</span>
                        </div>
                        {suspect.phoneNumber && (
                          <div>
                            <span className="font-medium text-gray-700">Phone:</span>
                            <span className="ml-2 text-gray-900 font-mono">{suspect.phoneNumber}</span>
                          </div>
                        )}
                        {suspect.customerId && (
                          <div>
                            <span className="font-medium text-gray-700">Customer ID:</span>
                            <span className="ml-2 text-gray-900 font-mono">{suspect.customerId}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Executive Summary */}
        <div className="mb-8">
          <h2 className="text-lg font-bold text-gray-900 mb-4 uppercase border-b border-gray-300 pb-2">Executive Summary</h2>
          <div className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
            {executiveSummary || <span className="text-gray-400 italic">Not provided</span>}
          </div>
        </div>

        {/* Details and Notes */}
        <div className="mb-8">
          <h2 className="text-lg font-bold text-gray-900 mb-4 uppercase border-b border-gray-300 pb-2">Details and Notes</h2>
          <div className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
            {narrativeDetails || <span className="text-gray-400 italic">Not provided</span>}
          </div>
        </div>

        {/* Conclusion */}
        <div className="mb-8">
          <h2 className="text-lg font-bold text-gray-900 mb-4 uppercase border-b border-gray-300 pb-2">Conclusion</h2>
          <div className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
            {narrativeConclusion || <span className="text-gray-400 italic">Not provided</span>}
          </div>
        </div>

        {/* Attachments */}
        {evidence.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-4 uppercase border-b border-gray-300 pb-2">Attachments</h2>
            <div className="space-y-2">
              {evidence.map((item, index) => (
                <div key={index} className="border border-gray-300 p-3 text-sm">
                  <div className="font-medium text-gray-900">{item.originalName}</div>
                  <div className="text-gray-600 mt-1">
                    Type: {item.fileType || 'N/A'} | Size: {(item.fileSize / 1024).toFixed(2)} KB
                  </div>
                  {item.description && (
                    <div className="text-gray-600 mt-1 italic">{item.description}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 pt-4 border-t border-gray-300 text-xs text-gray-600 text-center">
          Generated on {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} at {new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Narrative Investigation Report</h3>
        <div className="flex items-center space-x-3">
          {!isEditing && (
            <>
              <button
                onClick={() => setShowPreview(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                <Eye className="w-4 h-4" />
                <span>Preview</span>
              </button>
              <button
                onClick={generatePDF}
                className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                <Download className="w-4 h-4" />
                <span>Download PDF</span>
              </button>
            </>
          )}
          {canEdit && (
            <button
              onClick={() => {
                if (isEditing) {
                  handleSave();
                } else {
                  setIsEditing(true);
                }
              }}
              disabled={loading}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {isEditing ? (
                <>
                  <Save className="w-4 h-4" />
                  <span>{loading ? 'Saving...' : 'Save Report'}</span>
                </>
              ) : (
                <>
                  <Edit className="w-4 h-4" />
                  <span>Edit Report</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {isEditing ? (
        <div className="space-y-6">
          {/* Executive Summary */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Executive Summary <span className="text-red-500">*</span>
            </label>
            <textarea
              value={executiveSummary}
              onChange={(e) => setExecutiveSummary(e.target.value)}
              rows={6}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter executive summary of the investigation..."
            />
          </div>

          {/* Details and Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Details and Notes
            </label>
            <textarea
              value={narrativeDetails}
              onChange={(e) => setNarrativeDetails(e.target.value)}
              rows={10}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter detailed information and notes about the investigation..."
            />
          </div>

          {/* Conclusion */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Conclusion
            </label>
            <textarea
              value={narrativeConclusion}
              onChange={(e) => setNarrativeConclusion(e.target.value)}
              rows={6}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter conclusion of the investigation..."
            />
          </div>

          {/* Related Data Display */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-4">
            <h4 className="font-semibold text-gray-900">Related Information (Read-only)</h4>
            
            {/* Customers and Inmates */}
            {loadingData ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-sm text-gray-500 mt-2">Loading related data...</p>
              </div>
            ) : (
              <>
                {suspects.length > 0 && (
                  <div>
                    <h5 className="text-sm font-medium text-gray-700 mb-2">Related Parties</h5>
                    <div className="space-y-2">
                      {suspects.map((suspect, index) => (
                        <div key={index} className="bg-white p-3 rounded border border-gray-200">
                          {suspect.suspectType === 'Inmate' ? (
                            <div className="flex items-center space-x-2">
                              <User className="w-4 h-4 text-blue-600" />
                              <span className="text-sm font-medium">{suspect.inmateName}</span>
                              <span className="text-xs text-gray-500">- {suspect.facilityName}</span>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-2">
                              <Users className="w-4 h-4 text-green-600" />
                              <span className="text-sm font-medium">{suspect.customerName}</span>
                              {suspect.phoneNumber && (
                                <span className="text-xs text-gray-500">- {suspect.phoneNumber}</span>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Attachments */}
                {evidence.length > 0 && (
                  <div>
                    <h5 className="text-sm font-medium text-gray-700 mb-2">Attachments</h5>
                    <div className="space-y-2">
                      {evidence.map((item, index) => (
                        <div key={index} className="bg-white p-3 rounded border border-gray-200 flex items-center space-x-2">
                          <File className="w-4 h-4 text-blue-600" />
                          <span className="text-sm">{item.originalName}</span>
                          <span className="text-xs text-gray-500">({(item.fileSize / 1024).toFixed(2)} KB)</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="flex items-center justify-end space-x-4">
            <button
              onClick={() => {
                setIsEditing(false);
                // Reset to original values
                setExecutiveSummary(caseData?.executiveSummary || '');
                setNarrativeDetails(caseData?.narrativeDetails || '');
                setNarrativeConclusion(caseData?.narrativeConclusion || '');
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Save Report'}
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-gray-50 rounded-lg p-6">
          {executiveSummary || narrativeDetails || narrativeConclusion ? (
            <div className="space-y-6">
              {executiveSummary && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Executive Summary</h4>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap bg-white p-4 rounded">{executiveSummary}</p>
                </div>
              )}
              {narrativeDetails && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Details and Notes</h4>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap bg-white p-4 rounded">{narrativeDetails}</p>
                </div>
              )}
              {narrativeConclusion && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Conclusion</h4>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap bg-white p-4 rounded">{narrativeConclusion}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No narrative report yet</p>
              {canEdit && (
                <p className="text-sm text-gray-400 mt-2">Click "Edit Report" to create one</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between z-10">
              <h3 className="text-lg font-semibold text-gray-900">Report Preview</h3>
              <button
                onClick={() => setShowPreview(false)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4">
              {renderPreview()}
            </div>
            <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 flex items-center justify-end space-x-3">
              <button
                onClick={() => setShowPreview(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
              <button
                onClick={generatePDF}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                Download PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NarrativeTab;
